import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import * as R from 'ramda'
import { type Business, CRUD, PermissionArea } from '@pbt/pbt-ui-components'
import {
  Analytics,
  Calendar,
  Chat,
  ClientAndPatient,
  Communications,
  Home,
  Imaging,
  KnowledgeHub,
  Lab,
  Question,
  Settings,
  TasksDashboard,
  Whiteboard,
} from '@pbt/pbt-ui-components/src/icons'

import FeatureToggle from '~/constants/featureToggle'
import { NotificationAreaNames } from '~/constants/notifications'
import { useIsAgentChatEnabled } from '~/hooks/useIsAgentChatEnabled'
import {
  getCurrentBusiness,
  getCurrentBusinessIsIdexxImagingEnabled,
  getHasUserAccessToBenchmarkKeys,
} from '~/store/reducers/auth'
import { getFeatureToggle } from '~/store/reducers/constants'

import { useGeneralAnalyticsMenu } from './configs/useGeneralAnalyticsMenu'
import { useOmnichannelAnalyticsMenu } from './configs/useOmnichannelAnalyticsMenu'
import LeftMenuAgentButton from './LeftMenuAgentButton'
import LeftMenuHelpButton from './LeftMenuHelpButton'
import LeftMenuSupportButton from './LeftMenuSupportButton'
import type { MenuItem } from './menuItem'

const getMenu = R.pipe(R.filter(Boolean), R.filter(R.propOr(true, 'enabled')))

const useLeftMenuConfig = () => {
  const isSoapCustomizationEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SOAP_CUSTOMIZATION),
  )
  const isSsoPaymentTerminalsEnabled = useSelector(
    getFeatureToggle(FeatureToggle.SSO_PAYMENT_TERMINALS),
  )
  const isCvcRolesEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_ROLES),
  )
  const isRenameQuestionsAndChatEnabled = useSelector(
    getFeatureToggle(FeatureToggle.CVC_13939_UPDATE_RHAPSODY_NAV),
  )

  const currentBusiness = useSelector(getCurrentBusiness)
  const hasUserAccessToBenchmarkKeys = useSelector(
    getHasUserAccessToBenchmarkKeys(currentBusiness),
  )
  const { t } = useTranslation(['Common', 'Menu', 'Agent'])

  const isImagingIntegrated = useSelector(
    getCurrentBusinessIsIdexxImagingEnabled,
  )

  const generalAnalyticsMenu = useGeneralAnalyticsMenu()
  const omnichannelAnalyticsMenu = useOmnichannelAnalyticsMenu()
  const isAgentChatEnabled = useIsAgentChatEnabled()

  const AdminMenuGeneral: MenuItem[] = [
    ...(isSsoPaymentTerminalsEnabled && currentBusiness?.posPayEnabled
      ? [
          {
            id: '/admin/general/connected-devices',
            text: t('Menu:ADMIN_GENERAL.CONNECTED_DEVICES'),
            url: '/admin/general/connected-devices',
            permissionArea: !isCvcRolesEnabled
              ? PermissionArea.PERSON
              : undefined,
            permissionAreas: isCvcRolesEnabled
              ? [PermissionArea.PAYMENT, PermissionArea.PAYMENT_DEVICE]
              : undefined,
          },
        ]
      : []),
    {
      id: '/admin/general/appointment-configuration',
      text: t('Menu:ADMIN_GENERAL.APPOINTMENT_CONFIGURATION'),
      url: '/admin/general/appointment-configuration',
      permissionArea: PermissionArea.BUSINESS_APPOINTMENT_TYPE,
    },
    {
      id: '/admin/general/contacts',
      text: t('Menu:ADMIN_GENERAL.CONTACTS'),
      url: '/admin/general/contacts',
      permissionArea: PermissionArea.CONTACT,
    },
    {
      id: '/admin/general/marketplace',
      text: t('Menu:ADMIN_GENERAL.MARKETPLACE_AND_INTEGRATIONS'),
      url: '/admin/general/marketplace',
      permissionArea: isCvcRolesEnabled
        ? PermissionArea.MARKETPLACE_AND_INTEGRATIONS
        : PermissionArea.BUSINESS,
    },
    {
      id: '/admin/general/practices',
      text: t('Common:PRACTICE_OTHER'),
      url: '/admin/general/practices',
      permissionArea: PermissionArea.PRACTICES,
    },
    {
      id: '/admin/general/members',
      text: t('Common:TEAM_MEMBERS'),
      url: '/admin/general/members',
      permissionArea: PermissionArea.PERSON,
    },
    ...(isSoapCustomizationEnabled
      ? [
          {
            id: '/admin/general/soap-customization',
            text: t('Common:SOAP_CUSTOMIZATION'),
            url: '/admin/general/soap-customization',
            permissionArea: PermissionArea.SOAP_TEMPLATE,
          },
        ]
      : []),
    {
      id: '/admin/scheduling/availability',
      text: t('Common:SHIFTS_AND_PRACTICE_HOURS'),
      url: '/admin/scheduling/availability',
      permissionArea: PermissionArea.EVENT_AVAILABILITY,
    },
    {
      id: '/admin/general/spaces',
      text: t('Common:SPACE_OTHER'),
      url: '/admin/general/spaces',
      permissionArea: PermissionArea.SPACE,
    },
    ...(hasUserAccessToBenchmarkKeys
      ? [
          {
            id: '/admin/general/benchmarking',
            text: t('Common:BENCHMARKING_API_ACCESS'),
            url: '/admin/general/benchmarking',
          },
        ]
      : []),
  ]

  const AdminMenuSupport: MenuItem[] = [
    {
      id: '/admin/support/roles',
      text: t('Menu:ADMIN_SUPPORT.ROLES_MANAGEMENT'),
      url: '/admin/support/roles',
      permissionArea: PermissionArea.SUPPORT,
    },
  ]

  const AdminMenuCatalog: MenuItem[] = [
    {
      id: '/admin/catalog/bundles',
      text: t('Menu:ADMIN_CATALOG.BUNDLES'),
      url: '/admin/catalog/bundles',
      permissionArea: isCvcRolesEnabled
        ? PermissionArea.BUNDLE
        : PermissionArea.SOAP,
      permissionAction: isCvcRolesEnabled ? CRUD.UPDATE : undefined,
    },
    {
      id: '/admin/catalog/documents',
      text: t('Common:DOCUMENTS_AND_TEMPLATES'),
      url: '/admin/catalog/documents',
      permissionArea: PermissionArea.FORM,
    },
    {
      id: '/admin/catalog/inventories',
      text: t('Common:INVENTORY'),
      url: '/admin/catalog/inventories',
      permissionArea: PermissionArea.INVENTORY,
    },
    {
      id: '/admin/catalog/lab-tests',
      text: t('Common:LAB_TESTS'),
      url: '/admin/catalog/lab-tests',
      permissionArea: isCvcRolesEnabled
        ? PermissionArea.LAB_TEST_PRICE
        : PermissionArea.LAB_TEST,
    },
    {
      id: '/admin/catalog/pdmp-file-export',
      text: t(
        'Menu:ADMIN_CATALOG.PRESCRIPTION_DRUG_MONITORING_PROGRAM_FILE_EXPORT',
      ),
      url: '/admin/catalog/pdmp-file-export',
      permissionArea: PermissionArea.INVENTORY,
    },
    {
      id: '/admin/catalog/procedures',
      text: t('Common:PROCEDURE_OTHER'),
      url: '/admin/catalog/procedures',
      permissionArea: isCvcRolesEnabled
        ? PermissionArea.PROCEDURE_PRICE
        : PermissionArea.PROCEDURE,
    },
    {
      id: '/admin/catalog/questions',
      text: t('Common:QUESTION_OTHER'),
      url: '/admin/catalog/questions',
      permissionArea: isCvcRolesEnabled
        ? PermissionArea.CUSTOM_QUESTION
        : undefined,
    },
    {
      id: '/admin/catalog/reminder-protocols',
      text: t('Common:REMINDER_PROTOCOLS'),
      url: '/admin/catalog/reminder-protocols',
      permissionArea: PermissionArea.REMINDER_PROTOCOLS,
    },
    {
      id: '/admin/catalog/wellness-plans',
      text: t('Common:WELLNESS_PLANS'),
      url: '/admin/catalog/wellness-plans',
      disabled: (business: Business | undefined) =>
        !business?.wellnessPlansEnabled,
      permissionArea: PermissionArea.WELLNESS_PLAN_CONFIG,
    },
  ]

  const AdminMenu: MenuItem[] = [
    {
      id: 'admin-general',
      text: t('Menu:ADMIN.GENERAL'),
      expanded: true,
      children: getMenu(AdminMenuGeneral),
    },
    {
      id: 'admin-catalog',
      text: t('Common:CATALOG'),
      expanded: true,
      children: getMenu(AdminMenuCatalog),
    },
    {
      id: 'admin-support',
      text: t('Menu:ADMIN.SUPPORT'),
      hideIfNoPermissions: true,
      expanded: true,
      permissionArea: PermissionArea.SUPPORT,
      children: getMenu(AdminMenuSupport),
    },
  ]

  const MainMenu: MenuItem[] = [
    {
      id: '/landing',
      text: t('Menu:MAIN.DASHBOARD'),
      icon: Home,
      url: '/landing',
      permissionArea: PermissionArea.BUSINESS,
    },
    {
      id: '/clients',
      text: t('Common:CLIENTS_AND_PATIENTS'),
      icon: ClientAndPatient,
      url: '/clients',
      permissionArea: PermissionArea.PATIENT,
    },
    {
      id: '/scheduler',
      text: t('Menu:MAIN.SCHEDULER'),
      icon: Calendar,
      url: '/scheduler',
      permissionArea: PermissionArea.EVENT_APPOINTMENT,
    },
    {
      id: '/whiteboard',
      text: t('Menu:MAIN.WHITEBOARD'),
      icon: Whiteboard,
      url: '/whiteboard',
      permissionArea: PermissionArea.EVENT_APPOINTMENT,
    },
    {
      id: '/tasks-dashboard',
      text: t('Common:TASKS'),
      icon: TasksDashboard,
      url: '/tasks-dashboard',
      permissionArea: PermissionArea.TASK,
      notificationArea: NotificationAreaNames.TASKS,
    },
    {
      id: '/lab-tests-dashboard',
      text: t('Common:LAB_TESTS'),
      icon: Lab,
      url: '/lab-tests-dashboard',
      permissionArea: PermissionArea.LAB_TEST,
    },
    ...(isImagingIntegrated
      ? [
          {
            id: '/imaging-dashboard',
            text: t('Common:IMAGING_REQUESTS'),
            icon: Imaging,
            url: '/imaging-dashboard',
            permissionArea: PermissionArea.LAB_TEST,
          },
        ]
      : []),
    {
      id: '/communications',
      text: t('Common:COMMUNICATIONS'),
      icon: Communications,
      url: '/communications',
      permissionArea: PermissionArea.COMMUNICATION,
      notificationArea: NotificationAreaNames.COMMUNICATIONS,
    },
    {
      id: 'main-admin',
      text: t('Menu:MAIN.ADMIN'),
      icon: Settings,
      doNotRenderChildren: true,
      hideIfNoPermissions: true,
      children: getMenu(AdminMenu),
    },
    {
      id: 'main-analytics',
      text: t('Menu:MAIN.ANALYTICS_AND_REPORTS'),
      icon: Analytics,
      permissionArea: PermissionArea.REPORTING,
      hideIfNoPermissions: true,
      doNotRenderChildren: true,
      children: getMenu(
        currentBusiness?.omniChannel
          ? omnichannelAnalyticsMenu
          : generalAnalyticsMenu,
      ),
    },
    {
      id: 'main-knowledge-hub',
      text:
        isRenameQuestionsAndChatEnabled && currentBusiness?.omniChannel
          ? t('Menu:MAIN.KNOWLEDGE_HUB')
          : t('Menu:MAIN.TUTORIALS_AND_FAQS'),
      icon:
        isRenameQuestionsAndChatEnabled && currentBusiness?.omniChannel
          ? KnowledgeHub
          : Question,
      Component: LeftMenuSupportButton,
    },
    {
      id: 'main-chat-support',
      text:
        isRenameQuestionsAndChatEnabled && currentBusiness?.omniChannel
          ? t('Menu:MAIN.VIRTUAL_ASSISTANT')
          : t('Menu:MAIN.CHAT_WITH_SUPPORT'),
      icon: Chat,
      Component: LeftMenuHelpButton,
    },
  ]

  const BottomMenu: MenuItem[] = isAgentChatEnabled
    ? [
        {
          id: 'bottom-ai-assistant',
          text: t('Agent:AI_ASSISTANT'),
          icon: Chat,
          Component: LeftMenuAgentButton,
          isChatItem: true,
        },
      ]
    : []

  return {
    mainMenu: getMenu(MainMenu),
    bottomMenu: getMenu(BottomMenu),
  }
}

export default useLeftMenuConfig
